import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Pencil } from 'lucide-react';
import api from '../../lib/api';
import { fromSlug, toSlug } from '../../lib/slug';
import { Organization } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';

export default function AgencyBrandDetail() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState<{ name: string; type: 'AGENCY' | 'BRAND' }>({ name: '', type: 'BRAND' });

  useEffect(() => {
    fetchOrg();
  }, [id]);

  const fetchOrg = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/organizations/${id}`);
      setOrg(response.data);
    } catch (error) {
      console.error('Failed to load organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    if (!org) return;
    setEditData({ name: org.name, type: org.type });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!org) return;
    try {
      const response = await api.put(`/organizations/${org.id}`, {
        name: editData.name,
        type: editData.type,
      });
      setOrg(response.data);
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update organization:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent2 mx-auto"></div>
          <p className="mt-4 text-glass-text-2">Loading brand details...</p>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="text-center text-glass-text-2">Brand not found</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/agency/brands')}
            className="p-2 hover:bg-glass-3 rounded-lg text-glass-text transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-[1.75rem] font-semibold tracking-tightest text-glass-text">{org.name}</h1>
          <Badge variant="secondary">{org.type}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openEdit}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button onClick={() => navigate(`/agency/brands/${toSlug(org.name, org.id)}/team`)}>
            <Users className="w-4 h-4 mr-2" />
            Manage Team
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-accent2" />
            <h2 className="text-lg font-semibold text-glass-text">Organization Info</h2>
          </div>
          <div className="space-y-3 text-glass-text">
            <div><span className="font-medium text-glass-text-2">Name:</span> {org.name}</div>
            <div><span className="font-medium text-glass-text-2">Type:</span> {org.type}</div>
            <div><span className="font-medium text-glass-text-2">Created:</span> {new Date(org.createdAt).toLocaleString()}</div>
            <div><span className="font-medium text-glass-text-2">Updated:</span> {new Date(org.updatedAt).toLocaleString()}</div>
          </div>
        </div>

        <div className="glass-card p-6 col-span-2">
          <h2 className="text-lg font-semibold text-glass-text mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate(`/agency/brands/${toSlug(org.name, org.id)}/team`)}>
              <Users className="w-4 h-4 mr-2" />
              Manage Team Members
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit brand"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEdit}>Save Changes</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Brand name *</label>
            <Input
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              placeholder="e.g., Nike Malaysia"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Type *</label>
            <select
              value={editData.type}
              onChange={(e) => setEditData({ ...editData, type: e.target.value as 'AGENCY' | 'BRAND' })}
              className="glass-input w-full px-3 py-2 h-10 text-sm"
            >
              <option value="BRAND">Brand</option>
              <option value="AGENCY">Agency</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}